"use client"

import { Card, CardContent } from "@/components/ui/card"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { Clock, Mail, CreditCard, AlertCircle, CheckCircle } from "lucide-react"
import { HeroSection } from "@/components/ui/hero-section"
import { EnhancedButton } from "@/components/ui/enhanced-button"

export function RefundContent() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="relative z-10">
        {/* Header */}
        <section className="pt-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <BreadcrumbComponent
            items={[
              { label: "Home", href: "/" },
              { label: "Refund Policy" },
            ]}
          />
        </section>

        {/* Hero - Simplified to match site-wide minimal style */}
        <HeroSection
          size="minimal"
          showSparkle={false}
          showGlow={false}
          title="Refund Policy"
          subtitle="Our commitment to your satisfaction. We want you to be completely satisfied with your premium subscription."
        />

        {/* Main Content */}
        <div className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto pb-16">
          {/* Quick Summary */}
          <Card
            className="mb-8 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20"
          >
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    7-Day Web Money-Back Guarantee
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    Not satisfied within 7 days of your first paid web charge? We&apos;ll refund that payment in full, no questions asked.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Policy Sections */}
          <div className="space-y-8">
            {/* Section 1 */}
            <section>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                      <Clock className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Refund Eligibility
                    </h3>
                  </div>
                  <div className="pl-11 space-y-4 text-gray-600 dark:text-gray-400">
                    <p>
                      Web subscriptions are eligible for a full refund when requested <strong>within 7 days</strong> of the first paid charge. The free trial is not a paid charge.
                    </p>
                    <ul className="list-disc list-inside space-y-2">
                      <li>Refunds apply to the first billing cycle only</li>
                      <li>Subscription renewals are not eligible for refunds</li>
                      <li>Partial month refunds are not available after the 7-day period</li>
                    </ul>
                    <p>
                      App Store purchases are handled by Apple under Apple&apos;s refund policies.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Section 2 */}
            <section>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                      <Mail className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      How to Request a Refund
                    </h3>
                  </div>
                  <div className="pl-11 space-y-4 text-gray-600 dark:text-gray-400">
                    <p>
                      To request a web-subscription refund, email us at{' '}
                      <a href="mailto:support@whatgrassisthis.com" className="text-green-600 hover:underline">
                        support@whatgrassisthis.com
                      </a>{' '}
                      with:
                    </p>
                    <ul className="list-disc list-inside space-y-2">
                      <li>Your account email address</li>
                      <li>The reason for your refund request (optional, but helps us improve)</li>
                    </ul>
                    <p>
                      We aim to process all refund requests within <strong>3-5 business days</strong>.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Section 3 */}
            <section>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                      <CreditCard className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Refund Processing
                    </h3>
                  </div>
                  <div className="pl-11 space-y-4 text-gray-600 dark:text-gray-400">
                    <p>
                      Once approved, the refund returns to the original payment method. The time it takes to appear depends on the payment provider and your financial institution.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Section 4 */}
            <section>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                      <AlertCircle className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Cancellation vs. Refund
                    </h3>
                  </div>
                  <div className="pl-11 space-y-4 text-gray-600 dark:text-gray-400">
                    <p>
                      <strong>Cancellation</strong> and <strong>refund</strong> are different:
                    </p>
                    <ul className="list-disc list-inside space-y-2">
                      <li>
                        <strong>Cancel:</strong> Stops future billing. You keep access until your current period ends.
                      </li>
                      <li>
                        <strong>Refund:</strong> Returns your payment. Access ends immediately upon refund.
                      </li>
                    </ul>
                    <p>
                      You can cancel your subscription anytime from your{' '}
                      <a href="/auth/login?redirect=%2Fdashboard%2Fbilling" className="text-green-600 hover:underline">
                        Billing Settings
                      </a>.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Contact */}
            <section>
              <Card className="border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <CardContent className="pt-6 text-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Questions?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    We&apos;re here to help. Reach out anytime.
                  </p>
                  <a href="mailto:support@whatgrassisthis.com">
                    <EnhancedButton variant="hero-primary" size="md">
                      <Mail className="h-4 w-4 mr-2" />
                      Contact Support
                    </EnhancedButton>
                  </a>
                </CardContent>
              </Card>
            </section>
          </div>

          {/* Last Updated */}
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-8">
            Last updated: March 2026
          </p>
        </div>
      </div>
    </div>
  )
}
